import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, Check, X, Eye,
  Sparkles, UserCheck, Calendar, CheckCheck,
  MapPin, Mail, Star,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { api } from '../../../lib/api';

/**
 * KanbanView — Vue pipeline de la liste Candidats.
 *
 * 4 colonnes reflétant les étapes du pipeline avant Talent Pool :
 *   1. Nouveau           → new, cv_received, analyzed, qualification_pending
 *   2. Sélectionné       → validated, prequalified
 *   3. Entretien planif. → to_call
 *   4. Entretien fait    → interviewed, test_passed
 *
 * Chaque carte expose 4 actions (choix produit — pas de drag&drop pour l'instant) :
 *   - → Étape suivante (next stage, pas direct Talent Pool)
 *   - → Talent Pool (saut direct, même effet que le bouton "Garder" de la liste)
 *   - Rejeter
 *   - Voir profil
 */

const COLUMNS = [
  {
    id: 'new',
    title: 'Nouveau',
    subtitle: 'À analyser',
    icon: Sparkles,
    statuses: ['new', 'cv_received', 'analyzed', 'qualification_pending'],
    accent: 'bg-blue-50 border-blue-200 text-blue-800',
    iconBg: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'selected',
    title: 'Sélectionné',
    subtitle: 'Prêt à contacter',
    icon: UserCheck,
    statuses: ['validated', 'prequalified'],
    accent: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    iconBg: 'bg-cyan-100 text-cyan-700',
  },
  {
    id: 'scheduled',
    title: 'Entretien planifié',
    subtitle: 'RDV à venir',
    icon: Calendar,
    statuses: ['to_call'],
    accent: 'bg-amber-50 border-amber-200 text-amber-800',
    iconBg: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'done',
    title: 'Entretien fait',
    subtitle: 'À valider',
    icon: CheckCheck,
    statuses: ['interviewed', 'test_passed'],
    accent: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    iconBg: 'bg-emerald-100 text-emerald-700',
  },
];

const NEXT_STAGE = {
  new: 'validated',
  cv_received: 'validated',
  analyzed: 'validated',
  qualification_pending: 'validated',
  prequalified: 'validated',
  validated: 'to_call',
  to_call: 'interviewed',
  interviewed: 'talent_pool',
  test_passed: 'talent_pool',
};

const NEXT_STAGE_LABEL = {
  new: 'Sélectionné',
  cv_received: 'Sélectionné',
  analyzed: 'Sélectionné',
  qualification_pending: 'Sélectionné',
  prequalified: 'Sélectionné',
  validated: 'Entretien planif.',
  to_call: 'Entretien fait',
  interviewed: 'Talent Pool',
  test_passed: 'Talent Pool',
};

const KanbanView = ({ candidates, onMutated }) => {
  const grouped = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    (candidates || []).forEach((cand) => {
      const col = COLUMNS.find((c) => c.statuses.includes(cand.status));
      if (col) map[col.id].push(cand);
    });
    return map;
  }, [candidates]);

  const patchStatus = async (candidate, target, label) => {
    try {
      const res = await api.patch(`/admin/candidates/${candidate.id}/status`, {
        status: target,
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const onboardingOk = data?.onboarding?.ok === true;
        toast.success(
          `${candidate.first_name} → ${label}`,
          onboardingOk ? { description: "Email d'onboarding envoyé au candidat" } : undefined,
        );
        onMutated?.(candidate.id, target);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Échec de la mise à jour');
      }
    } catch {
      toast.error('Erreur réseau');
    }
  };

  const reject = async (candidate) => {
    try {
      const res = await api.post(`/admin/candidates/${candidate.id}/reject`);
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const emailOk = data?.rejection_email?.ok === true;
        toast.success(
          emailOk ? 'Candidat rejeté · email envoyé' : 'Candidat rejeté · email non envoyé',
          { duration: 2500 },
        );
        onMutated?.(candidate.id, 'rejected');
      } else {
        toast.error('Échec du rejet');
      }
    } catch {
      toast.error('Erreur réseau');
    }
  };

  return (
    <div className="overflow-x-auto pb-4" data-testid="candidates-kanban">
      <div className="flex gap-4 min-w-max px-1">
        {COLUMNS.map((col) => {
          const Icon = col.icon;
          const items = grouped[col.id] || [];
          return (
            <div
              key={col.id}
              className="w-80 shrink-0"
              data-testid={`kanban-col-${col.id}`}
            >
              <div className={`rounded-t-xl border-2 border-b-0 ${col.accent} px-3 py-3 flex items-center justify-between sticky top-0 z-10`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-md ${col.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm leading-tight truncate">{col.title}</div>
                    <div className="text-[11px] opacity-70 truncate">{col.subtitle}</div>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs tabular-nums bg-white/70">
                  {items.length}
                </Badge>
              </div>
              <div className="rounded-b-xl border-2 border-t-0 border-gray-200 bg-gray-50/50 p-2 space-y-2 min-h-[200px]">
                {items.length === 0 ? (
                  <div className="text-center text-xs text-gray-400 py-8 italic">
                    Aucun candidat
                  </div>
                ) : (
                  items.map((cand) => (
                    <KanbanCard
                      key={cand.id}
                      candidate={cand}
                      onAdvance={() => {
                        const next = NEXT_STAGE[cand.status] || 'talent_pool';
                        const label = NEXT_STAGE_LABEL[cand.status] || 'Talent Pool';
                        patchStatus(cand, next, label);
                      }}
                      onPromote={() => patchStatus(cand, 'talent_pool', 'Talent Pool')}
                      onReject={() => reject(cand)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const KanbanCard = ({ candidate, onAdvance, onPromote, onReject }) => {
  const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Candidat';
  const score = candidate.qualification?.score_total || candidate.ai_analysis?.candidate_score || null;
  const profession = candidate.main_profession || candidate.primary_category || '';
  const country = candidate.country_name || candidate.country || '';
  const nextLabel = NEXT_STAGE_LABEL[candidate.status];
  const isTerminalStage = !NEXT_STAGE[candidate.status] || NEXT_STAGE[candidate.status] === 'talent_pool';

  return (
    <Card
      className="p-3 border border-gray-200 hover:border-tp-accent/40 hover:shadow-md transition-all bg-white"
      data-testid={`kanban-card-${candidate.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-tp-text truncate">{fullName}</div>
          {profession && (
            <div className="text-[11px] text-gray-500 truncate">{profession}</div>
          )}
        </div>
        {score !== null && score > 0 && (
          <div className="flex items-center gap-1 text-xs font-medium text-tp-primary tabular-nums flex-shrink-0">
            <Star className="h-3 w-3 fill-tp-accent text-tp-accent" />
            {score}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500 mb-2.5">
        {country && (
          <span className="inline-flex items-center gap-0.5">
            <MapPin className="h-3 w-3" />
            {country}
          </span>
        )}
        {candidate.email && (
          <span className="inline-flex items-center gap-0.5 truncate max-w-[140px]">
            <Mail className="h-3 w-3" />
            {candidate.email}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        {!isTerminalStage && nextLabel ? (
          <Button
            onClick={onAdvance}
            size="sm"
            variant="outline"
            className="h-7 text-[11px] px-2 col-span-2 border-gray-300 hover:border-tp-primary hover:bg-tp-primary/5"
            data-testid={`kanban-advance-${candidate.id}`}
          >
            <ArrowRight className="h-3 w-3 mr-1" />
            → {nextLabel}
          </Button>
        ) : null}
        <Button
          onClick={onPromote}
          size="sm"
          className="h-7 text-[11px] px-2 bg-tp-accent hover:bg-tp-accent/90 text-white col-span-2"
          data-testid={`kanban-promote-${candidate.id}`}
        >
          <Check className="h-3 w-3 mr-1" />
          Talent Pool
        </Button>
        <Button
          onClick={onReject}
          size="sm"
          variant="outline"
          className="h-7 text-[11px] px-2 border-red-200 text-red-600 hover:bg-red-50"
          data-testid={`kanban-reject-${candidate.id}`}
        >
          <X className="h-3 w-3 mr-1" />
          Rejeter
        </Button>
        <Link
          to={`/admin/candidates/${candidate.id}`}
          className="inline-flex items-center justify-center h-7 text-[11px] px-2 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
          data-testid={`kanban-view-${candidate.id}`}
        >
          <Eye className="h-3 w-3 mr-1" />
          Voir
        </Link>
      </div>
    </Card>
  );
};

export default KanbanView;
